import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import Iconify from "../components/iconify";
import { get } from "src/utils/networkUtils";
import { fDateTime } from "src/utils/formatTime";
// ----------------------------------------------------------------------

export default function PromotionRewardsPage() {
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const [loading, setLoading] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [promotion, setPromotion] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const limit = 20;

  useEffect(() => {
    if (promotionId) {
      // Fetch promotion details (only once)
      get(`/admin/promotions/${promotionId}`)
        .then(function (response) {
          setPromotion(response.promotion);
        })
        .catch(function (error) {
          console.error('Error fetching promotion:', error);
        });
    }
  }, [promotionId]);

  useEffect(() => {
    if (promotionId) {
      setLoading(true);
      console.log('Fetching promotion rewards for ID:', promotionId, 'offset:', offset);

      // Fetch promotion rewards with pagination
      get(`/admin/promotions/${promotionId}/rewards?offset=${offset}&limit=${limit}`)
        .then(function (response) {
          setRewards((prev) =>
            offset === 0
              ? response.rewards || []
              : [...prev, ...(response.rewards || [])]
          );
          setHasMore((response.rewards || []).length === limit);
          setLoading(false);
        })
        .catch(function (error) {
          console.error('Error fetching promotion rewards:', error);
          setLoading(false);
        });
    }
  }, [promotionId, offset]);

  const handleBackToPromotions = () => {
    navigate('/dashboard/promotions');
  };

  const handleUserProfile = (userId) => {
    navigate(`/dashboard/profile?user_id=${userId}`);
  };

  const lastRewardRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + limit);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, limit]
  );

  return (
    <>
      <Helmet>
        <title>Promotion Rewards</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <div>
            <Typography variant="h4" gutterBottom>
              Promotion Rewards
            </Typography>
            {promotion && (
              <Typography variant="h6" color="text.secondary">
                {promotion.title}
              </Typography>
            )}
          </div>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={handleBackToPromotions}
          >
            Back to Promotions
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="rewards table">
                <TableHead>
                  <TableRow>
                    <TableCell>Reward ID</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Reward Amount</TableCell>
                    <TableCell>Date Rewarded</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rewards.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No rewards found for this promotion
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rewards.map((reward, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                        ref={
                          index === rewards.length - 1
                            ? lastRewardRef
                            : null
                        }
                      >
                        <TableCell component="th" scope="row">
                          {reward.rewardId}
                        </TableCell>
                        <TableCell>{reward.userId}</TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleUserProfile(reward.userId)}
                            sx={{
                              textDecoration: 'none',
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {reward.username}
                          </Link>
                        </TableCell>
                        <TableCell>{reward.rewardAmount}</TableCell>
                        <TableCell>
                          {fDateTime(reward.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {loading && rewards?.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {rewards?.length === 0 && loading && (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          )}
        </Card>
      </Container>
    </>
  );
}
